import { useEffect, useRef, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { useAppSelector } from '@store/hooks';
import { selectAuth } from '@store/slices/auth';
import { Logo } from '@components/ui/Logo';

// Timings tuned for "fast but not flashing":
// - MIN_DISPLAY: just long enough to avoid a jarring flash if Firebase resolves
//   in ~50ms, but short enough that startup feels instant (was 900ms, which
//   added a visible ~0.9s hold on every boot even when auth was already ready).
// - FADE_OUT:   quick, smooth exit.
const MIN_DISPLAY_MS = 150;
const FADE_OUT_MS = 250;

// Material's "standard" easing curve.
const SMOOTH_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

type Phase = 'visible' | 'fading' | 'hidden';

/**
 * Full-viewport branded overlay shown during the Firebase auth boot window.
 *
 * No fade-IN: the splash mounts instantly at full opacity. A previous version
 * faded in from opacity 0, which created a brief transparent window where
 * underlying route content (notably HomeScreen) flashed before the splash
 * appeared. Boot is fast enough that an entrance animation isn't worth that
 * cost — only the EXIT needs to be smooth, and that's the user-perceived
 * "splash → home" transition anyway.
 *
 * Phase machine: visible → fading → hidden. Once hidden, never re-renders;
 * sign-out flips `isAuthed` but not `initializing`, so this stays unmounted.
 */
export function BootSplash() {
  const { initializing } = useAppSelector(selectAuth);
  const [phase, setPhase] = useState<Phase>(initializing ? 'visible' : 'hidden');
  const mountTime = useRef(Date.now());

  // Once auth is ready AND we've been visible long enough, fade out.
  useEffect(() => {
    if (phase !== 'visible' || initializing) return;
    const elapsed = Date.now() - mountTime.current;
    const wait = Math.max(0, MIN_DISPLAY_MS - elapsed);
    const t = setTimeout(() => setPhase('fading'), wait);
    return () => clearTimeout(t);
  }, [phase, initializing]);

  // Unmount after the exit transition completes.
  useEffect(() => {
    if (phase !== 'fading') return;
    const t = setTimeout(() => setPhase('hidden'), FADE_OUT_MS);
    return () => clearTimeout(t);
  }, [phase]);

  if (phase === 'hidden') return null;

  const isFading = phase === 'fading';

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading workspace"
      className={`fixed inset-0 z-[9999] flex items-center justify-center bg-background ${
        isFading ? 'pointer-events-none' : ''
      }`}
      style={{
        opacity: isFading ? 0 : 1,
        transition: `opacity ${FADE_OUT_MS}ms ${SMOOTH_EASE}`,
      }}
    >
      <div
        className="flex flex-col items-center gap-5"
        style={{
          // Subtle exit scale gives the splash a sense of "release". On entry
          // we just snap in (no scale-up) — entry animation isn't needed.
          transform: isFading ? 'scale(0.96)' : 'scale(1)',
          transition: `transform ${FADE_OUT_MS}ms ${SMOOTH_EASE}`,
        }}
      >
        <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-white shadow-soft ring-1 ring-border">
          <Logo variant="mark" height={44} />
        </div>
        <div className="text-center leading-tight">
          <div className="text-[22px] font-semibold tracking-tight text-foreground">
            Trendz
          </div>
          <div className="mt-0.5 text-[11px] font-medium uppercase tracking-[0.22em] text-foreground-muted">
            Master Data
          </div>
        </div>
        <CircularProgress size={24} thickness={4} />
        <div className="text-[13px] text-foreground-muted">
          Loading your workspace…
        </div>
      </div>
    </div>
  );
}
